import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { INR, fmtDate, titleCase, StatusBadge } from "@/lib/format";
import { PageHeader, DataTable, Pager, useList } from "@/components/shared";
import { FormDialog } from "@/components/EntityForm";
import { ChangeExpiryDialog, ChangeHistoryDialog } from "@/components/ControlledEdit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Plus, Search, RefreshCw, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PSTATUS = ["quote", "proposal", "booked", "payment_pending", "issued", "active", "expired", "cancelled"];

export default function Policies() {
  const { hasPerm } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [insurers, setInsurers] = useState([]);
  const [customers, setCustomers] = useState([]);

  const params = { ...(q ? { q } : {}), ...(status !== "all" ? { status } : {}) };
  const { items, total, loading, page, setPage, refetch } = useList("/policies", params);
  useEffect(() => {
    api.get("/insurers").then(({ data }) => setInsurers(data)).catch(() => {});
    api.get("/customers", { params: { limit: 100 } }).then(({ data }) => setCustomers(data.items || [])).catch(() => {});
  }, []);

  const create = async (v) => { await api.post("/policies", v); refetch(); };
  const renew = async (pol, v) => { await api.post(`/policies/${pol.id}/renew`, v); refetch(); };

  const baseFields = (c) => [
    { name: "customer_id", label: "Customer", type: "select", required: true, options: customers.map((x) => ({ value: x.id, label: `${x.name} (${x.customer_id})` })) },
    { name: "insurer", label: "Insurer", type: "select", required: true, options: insurers.map((x) => ({ value: x.name, label: x.name })) },
    { name: "product", label: "Product", type: "select", options: ["Car - Comprehensive", "Car - Third Party", "Car - Own Damage", "Bike - Comprehensive", "Bike - Third Party", "Health", "Life"] },
    { name: "policy_number", label: "Policy Number" },
    { name: "registration_number", label: "Vehicle Reg No" },
    { name: "premium", label: "Premium (₹)", type: "number", required: true },
    { name: "sum_insured", label: "Sum Insured / IDV (₹)", type: "number" },
    { name: "start_date", label: "Start Date", type: "date" },
    { name: "expiry_date", label: "Expiry Date", type: "date" },
    { name: "policy_status", label: "Status", type: "select", options: PSTATUS.map((s) => ({ value: s, label: titleCase(s) })) },
    { name: "payment_mode", label: "Payment Mode", type: "select", options: ["UPI", "Card", "NetBanking", "Cheque", "Cash"] },
  ];

  const renewFields = [
    { name: "policy_number", label: "New Policy Number" },
    { name: "premium", label: "New Premium (₹)", type: "number" },
    { name: "start_date", label: "Start Date", type: "date" },
    { name: "expiry_date", label: "Expiry Date", type: "date" },
  ];

  const columns = [
    { key: "policy_number", header: "Policy No", render: (r) => <span className="font-mono text-xs">{r.policy_number}</span> },
    { key: "customer_name", header: "Customer", render: (r) => <span className="font-semibold">{r.customer_name || "—"}</span> },
    { key: "insurer", header: "Insurer", render: (r) => <span className="text-sm">{r.insurer}</span> },
    { key: "product", header: "Product", render: (r) => <span className="text-sm text-muted-foreground">{r.product}</span> },
    { key: "premium", header: "Premium", tdClass: "text-right", thClass: "text-right", render: (r) => <span className="tabular font-semibold">{INR(r.premium)}</span> },
    { key: "expiry_date", header: "Expiry", render: (r) => <span className="text-sm">{fmtDate(r.expiry_date)}</span> },
    { key: "policy_status", header: "Status", render: (r) => <StatusBadge value={r.policy_status} /> },
    { key: "actions", header: "Actions", render: (r) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        {r.customer_id && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => nav(`/customers/${r.customer_id}`)} title="View 360"><Eye className="h-4 w-4" /></Button>}
        {hasPerm("policies:add") && (
          <FormDialog title={`Renew ${r.policy_number}`} description="Creates a NEW policy; the old one is preserved in history." fields={renewFields} onSubmit={(v) => renew(r, v)} submitLabel="Create Renewal"
            trigger={<Button size="icon" variant="ghost" className="h-8 w-8 text-primary" title="Renew" data-testid="policy-renew-btn"><RefreshCw className="h-4 w-4" /></Button>} />
        )}
        {hasPerm("policies:edit_expiry") && <ChangeExpiryDialog policy={r} onDone={refetch} />}
        {(hasPerm("audit:view") || hasPerm("policies:edit_expiry")) && <ChangeHistoryDialog entityId={r.id} />}
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Policies" subtitle={`${total} policies · renewal chains preserved`} icon={ShieldCheck}
        actions={hasPerm("policies:add") && (
          <FormDialog title="New Policy" fields={baseFields()} onSubmit={create} submitLabel="Create Policy" testId="create-policy-dialog"
            trigger={<Button data-testid="create-policy-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Policy</Button>} />
        )} />
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search policy no, reg no, customer..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="policy-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44" data-testid="policy-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{PSTATUS.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent></Select>
      </div>
      <DataTable columns={columns} rows={items} loading={loading} rowTestId="policy-table-row" />
      <Pager page={page} limit={25} total={total} onPage={setPage} />
    </div>
  );
}
